import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, type Column } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import { downloadBlob } from "../../../utils/csvExport.js";
import { formatDateTime } from "../../../utils/index.js";
import { AWARDS_WRITE_PERMISSION } from "../../../constants/permissions.js";
import { awards } from "../../../api/awards.service.js";
import type { AwardDictionaryPruneResponse } from "../../../api/awards.types.js";
import {
  awardsImportService,
  type ImportRequestError,
  type ImportRun,
  type ImportSummary,
} from "../../../api/awardsImport.service.js";
import styles from "./AwardsImport.module.css";

/**
 * Uploads the awards workbook and follows the resulting import run. The run
 * is long (minutes), so the page attaches to whichever run is in progress —
 * its own or one another operator started — and polls until it finishes,
 * then shows the summary and offers the per-row CSV report.
 *
 * Dry run is the default: the importer reports what it would do and writes
 * nothing. Clearing existing awards is destructive and only meaningful on a
 * live run, so it is disabled under dry run and confirmed explicitly.
 */

export const POLL_INTERVAL_MS = 5_000;

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const INCOMPLETE_REASON_LABELS: Record<string, string> = {
  firm_unmatched: "Firm not matched",
  firm_ambiguous: "Firm ambiguous",
  lawyer_unmatched: "Lawyer not matched",
  lawyer_ambiguous: "Lawyer ambiguous",
  name_only: "Name only",
  match_error: "Match error",
};

/**
 * Mirrors the rules of awards-api's category cell resolver
 * (models/awards-category-resolver.ts). Keys the resolver no longer emits stay so that runs
 * stored earlier keep their labels.
 */
const CATEGORY_RULE_LABELS: Record<string, string> = {
  whole_type: "Whole cell is an award type",
  split_known_type: "Category + known award type",
  split_new_type: "Category + new award type",
  // Runs stored before the rule was renamed to `category_only` still carry this key.
  category_only_fallback: "Category only (kept as award type)",
  // awards-api rejects such rows and reports them under "Skipped category-only cells", so
  // this key is only a safety net.
  category_only: "Category only (row skipped)",
  no_category_new_type: "No category, new award type",
};

const STATUS_LABELS: Record<ImportRun["status"], string> = {
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
};

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Row counts are only meaningful for the matching stage, and only once a total is known. */
function runningLabel(progress: ImportRun["progress"]): string {
  if (!progress) return "Import running… this usually takes about 10 minutes";
  if (progress.stage === "parsing") return "Parsing file…";
  if (progress.stage === "importing") return "Writing awards…";
  return progress.total > 0
    ? `Matching rows ${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`
    : "Matching rows…";
}

/** The report is the importer's per-row output, so only a completed run has one. */
function hasReport(run: ImportRun): boolean {
  return run.status === "succeeded";
}

function reportFilename(run: ImportRun): string {
  const base = run.fileName.replace(/\.[^.]+$/, "") || "awards-import";
  return `${base}-report-${run.id}.csv`;
}

function StatusPill({ status }: { status: ImportRun["status"] }) {
  const tone =
    status === "succeeded"
      ? styles.pillOk
      : status === "failed"
        ? styles.pillError
        : styles.pillInfo;
  return <span className={`${styles.pill} ${tone}`}>{STATUS_LABELS[status]}</span>;
}

type SummaryRow = { key: string; label: string; value: number; indent?: boolean };

/**
 * Flattens the summary into labelled rows, skipping anything the run did not
 * record. Incomplete reasons sit under the incomplete total, category
 * resolution rules under the category cell resolution total and the new award
 * types under their own count; a reason or rule the labels do not know is shown
 * by its raw key rather than dropped.
 */
function summaryRows(summary: ImportSummary, clearExisting: boolean): SummaryRow[] {
  const rows: SummaryRow[] = [];
  const push = (key: string, label: string, value: number | undefined, indent = false) => {
    if (typeof value === "number") rows.push({ key, label, value, indent });
  };
  push("importable", "Matched (published)", summary.imported?.importable);
  push("incompleteTotal", "Incomplete (draft)", summary.incompleteTotal);
  for (const [reason, count] of Object.entries(summary.incompleteByReason ?? {})) {
    push(`reason:${reason}`, INCOMPLETE_REASON_LABELS[reason] ?? reason, count, true);
  }
  push("skippedFirmLevel", "Skipped firm-level rows", summary.skippedFirmLevel);
  push("skippedCategoryOnly", "Skipped category-only cells", summary.skippedCategoryOnly);
  push("skippedUnknownAwardId", "Skipped unknown award ids", summary.skippedUnknownAwardId);
  push("inserted", "Inserted", summary.imported?.inserted);
  push("updated", "Updated", summary.imported?.updated);
  push("duplicatesCollapsed", "Duplicates collapsed", summary.duplicatesCollapsed);
  push("parserRowErrors", "Parser row errors", summary.parserRowErrors);
  push("distinctCategoriesCount", "Distinct categories", summary.distinctCategoriesCount);
  if (summary.categoryResolution) {
    const resolvedRows = Object.values(summary.categoryResolution).reduce(
      (sum, count) => sum + count,
      0,
    );
    push("categoryResolution", "Category cell resolution", resolvedRows);
    for (const [rule, count] of Object.entries(summary.categoryResolution)) {
      push(`categoryRule:${rule}`, CATEGORY_RULE_LABELS[rule] ?? rule, count, true);
    }
  }
  if (summary.newAwardTypes) {
    push(
      "newAwardTypes",
      "New award types (not on the reference list)",
      summary.newAwardTypes.length,
    );
    for (const entry of summary.newAwardTypes) {
      push(`newAwardType:${entry.type}`, entry.type, entry.count, true);
    }
  }
  if (clearExisting) {
    push("prunedAwardTypes", "Unused award types removed", summary.prunedAwardTypes);
    push("prunedCategories", "Unused categories removed", summary.prunedCategories);
  }
  return rows;
}

function pruneResultMessage(result: AwardDictionaryPruneResponse): string {
  const types = result.prunedAwardTypes === 1 ? "award type" : "award types";
  const categories = result.prunedCategories === 1 ? "category" : "categories";
  return `Removed ${result.prunedAwardTypes.toLocaleString()} ${types} and ${result.prunedCategories.toLocaleString()} ${categories}`;
}

export default function AwardsImport() {
  const { hasPermission } = usePermissions();
  const canUse = hasPermission("developer.write");
  const canPrune = hasPermission(AWARDS_WRITE_PERMISSION);

  const [runs, setRuns] = useState<ImportRun[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [clearExisting, setClearExisting] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmingPrune, setIsConfirmingPrune] = useState(false);
  const [isPruning, setIsPruning] = useState(false);
  const [pruneResult, setPruneResult] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const latest = runs[0] ?? null;
  const runningId = latest?.status === "running" ? latest.id : null;

  const refresh = useCallback(async () => {
    try {
      setRuns(await awardsImportService.list());
    } catch (listError) {
      setError(errorMessage(listError));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!runningId) return;
    const timer = setInterval(async () => {
      try {
        const run = await awardsImportService.getRun(runningId);
        if (run.status === "running") {
          setRuns(current => current.map(item => (item.id === run.id ? run : item)));
        } else {
          // The run list is the source of truth once the run has finished.
          await refresh();
        }
      } catch {
        // A missed poll is harmless: the next tick asks again.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [runningId, refresh]);

  const startImport = useCallback(async () => {
    if (!file) return;
    setIsConfirming(false);
    setError(null);
    setNotice(null);
    setIsStarting(true);
    try {
      const run = await awardsImportService.start(file, { dryRun, clearExisting });
      setRuns(current => [run, ...current.filter(item => item.id !== run.id)]);
      setFile(null);
    } catch (startError) {
      if ((startError as ImportRequestError).status === 409) {
        setNotice("An import is already running");
        await refresh();
      } else {
        setError(errorMessage(startError));
      }
    } finally {
      setIsStarting(false);
    }
  }, [file, dryRun, clearExisting, refresh]);

  const pruneDictionaries = useCallback(async () => {
    setIsConfirmingPrune(false);
    setError(null);
    setPruneResult(null);
    setIsPruning(true);
    try {
      setPruneResult(pruneResultMessage(await awards.pruneDictionaries()));
    } catch (pruneError) {
      setError(errorMessage(pruneError));
    } finally {
      setIsPruning(false);
    }
  }, []);

  const saveReport = useCallback(async (run: ImportRun) => {
    setError(null);
    try {
      downloadBlob(await awardsImportService.downloadReport(run.id), reportFilename(run));
    } catch (downloadError) {
      setError(errorMessage(downloadError));
    }
  }, []);

  const historyRows = useMemo(() => runs.filter(run => run.id !== latest?.id), [runs, latest]);

  const historyColumns = useMemo<Column<ImportRun>[]>(
    () => [
      {
        header: "Status",
        accessor: "status",
        render: (_value, row) => <StatusPill status={row.status} />,
      },
      {
        header: "Started",
        accessor: "startedAt",
        render: (_value, row) => formatDateTime(row.startedAt),
      },
      {
        header: "By",
        accessor: "startedBy",
        render: (_value, row) => row.startedBy ?? "—",
      },
      { header: "File", accessor: "fileName", wrap: true },
      {
        header: "Dry run",
        accessor: "dryRun",
        render: (_value, row) => (row.dryRun ? "Yes" : "No"),
      },
      {
        header: "Cleared",
        accessor: "clearExisting",
        render: (_value, row) => (row.clearExisting ? "Yes" : "No"),
      },
      {
        header: "Report",
        accessor: "id",
        render: (_value, row) =>
          hasReport(row) ? (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => void saveReport(row)}
            >
              Download report
            </button>
          ) : (
            "—"
          ),
      },
    ],
    [saveReport],
  );

  if (!canUse) {
    return <UnauthorizedView message="You do not have permission to use the awards import." />;
  }

  const isRunning = runningId !== null;
  const formDisabled = isRunning || isStarting;
  const finished = latest && latest.status !== "running" ? latest : null;
  const rows = finished?.summary
    ? summaryRows(finished.summary, finished.clearExisting && !finished.dryRun)
    : [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Awards Import"
        subtitle="Upload the awards workbook and import it into the awards database"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className={`${styles.banner} ${styles.bannerWarning}`} role="status">
          {notice}
        </div>
      ) : null}
      {pruneResult ? (
        <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
          {pruneResult}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>Upload</div>
        <form
          className={styles.form}
          onSubmit={event => {
            event.preventDefault();
            if (clearExisting && !dryRun) {
              setIsConfirming(true);
            } else {
              void startImport();
            }
          }}
        >
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Awards workbook (.xlsx)</span>
            <input
              type="file"
              name="file"
              accept={`.xlsx,${XLSX_MIME}`}
              disabled={formDisabled}
              onChange={event => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          <Checkbox
            label="Dry run (report only, no changes)"
            aria-label="Dry run (report only, no changes)"
            checked={dryRun}
            disabled={formDisabled}
            onChange={event => {
              setDryRun(event.target.checked);
              // Clearing is a live-run decision; a dry run never clears.
              if (event.target.checked) setClearExisting(false);
            }}
          />
          <Checkbox
            label="Clear all existing awards before import"
            aria-label="Clear all existing awards before import"
            checked={clearExisting}
            disabled={formDisabled || dryRun}
            onChange={event => setClearExisting(event.target.checked)}
          />
          <div className={styles.toolbar}>
            <Button type="submit" variant="contained" disabled={formDisabled || !file}>
              Run import
            </Button>
            {canPrune ? (
              <Button
                type="button"
                variant="outlined"
                disabled={formDisabled || isPruning}
                onClick={() => setIsConfirmingPrune(true)}
              >
                Prune unused dictionary values
              </Button>
            ) : null}
            {!dryRun ? (
              <span className={styles.rowNote}>Live run: awards will be written.</span>
            ) : null}
          </div>
        </form>
      </div>

      {isRunning && latest ? (
        <div className={styles.card}>
          <div className={styles.running} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <span>{runningLabel(latest.progress)}</span>
          </div>
          <div className={styles.rowNote}>
            {latest.fileName}
            {latest.startedBy ? ` · started by ${latest.startedBy}` : ""}
            {` · ${formatDateTime(latest.startedAt)}`}
            {latest.dryRun ? " · dry run" : ""}
          </div>
        </div>
      ) : null}

      {finished ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Latest run</div>
          <div className={styles.toolbar}>
            <StatusPill status={finished.status} />
            {finished.dryRun ? <span className={styles.tag}>Dry run</span> : null}
            {finished.clearExisting ? <span className={styles.tag}>Cleared existing</span> : null}
          </div>
          {finished.error ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {finished.error}
            </div>
          ) : null}
          <dl className={styles.meta}>
            <dt>File</dt>
            <dd>{finished.fileName}</dd>
            <dt>Started by</dt>
            <dd>{finished.startedBy ?? "—"}</dd>
            <dt>Started at</dt>
            <dd>{formatDateTime(finished.startedAt)}</dd>
            <dt>Finished at</dt>
            <dd>{finished.finishedAt ? formatDateTime(finished.finishedAt) : "—"}</dd>
          </dl>
          {rows.length > 0 ? (
            <table className={styles.summary} aria-label="Import summary">
              <tbody>
                {rows.map(row => (
                  <tr key={row.key} className={row.indent ? styles.indent : undefined}>
                    <th scope="row">{row.label}</th>
                    <td>{row.value.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <div className={styles.toolbar}>
            <span className={styles.spacer} />
            <Button
              variant="outlined"
              disabled={!hasReport(finished)}
              onClick={() => void saveReport(finished)}
            >
              Download report CSV
            </Button>
          </div>
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>Previous runs</div>
        <DataTable
          data={historyRows}
          columns={historyColumns}
          rowKey="id"
          emptyMessage="No previous runs."
        />
      </div>

      {isConfirming ? (
        <ConfirmationModal
          title="Clear all existing awards"
          message="Every award currently in the database will be deleted before the workbook is imported."
          warningMessage="This cannot be undone. Awards that are not in the workbook will be gone."
          confirmButtonText="Clear and import"
          confirmButtonColor="error"
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={startImport}
        />
      ) : null}

      {isConfirmingPrune ? (
        <ConfirmationModal
          title="Prune unused dictionary values"
          message="Award types and categories that no award uses will be deleted. Series and products are not affected."
          warningMessage="This cannot be undone. The removed values will no longer be offered in the award form."
          confirmButtonText="Prune"
          confirmButtonColor="error"
          backdropClick={false}
          onClose={() => setIsConfirmingPrune(false)}
          onConfirm={pruneDictionaries}
        />
      ) : null}
    </div>
  );
}
